import { useState } from 'react';

import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { DownloadIcon } from '@/shared/ui/icons';
import { Input, Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import { formatDate } from '@/shared/utils/format';

import { detailText, endOfDay, startOfDay, untilAsDay } from './accessLogHelpers';
import { ACCESS_LOG_PAGE, accessLogCsvUrl } from './api';
import { type AccessLogEntry, type AccessLogFilters, EVENTS, eventLabel } from './types';
import { useAccessLog, useAdminUsers } from './useAdmin';

const dateTimeFormat = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'short',
  timeStyle: 'medium',
});

/**
 * The access log of the contract (Anexo II §4.c): who entered, who failed, who changed
 * what. Newest first, filterable by person, event and dates, and the same rows as a CSV
 * for whoever asks for it on paper.
 *
 * The filters are one row above the table, the CSV link at its right end; what the log
 * is and how long it is kept is in the header's help.
 *
 * The date filters are days, not instants: «hasta» includes the whole day the person
 * picked, because nobody means «until midnight at the start of the 12th» when they type
 * the 12th.
 */
export function AccessLogTab() {
  const [filters, setFilters] = useState<AccessLogFilters>({});
  const log = useAccessLog(filters);
  const users = useAdminUsers();

  function change(patch: AccessLogFilters) {
    setFilters((current) => ({ ...current, ...patch, offset: 0 }));
  }

  const total = log.data?.total ?? 0;
  const offset = filters.offset ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + ACCESS_LOG_PAGE, total);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Quién entró en la aplicación, quién falló la contraseña y quién cambió qué en esta pantalla.
        Se conserva doce meses. «Descargar CSV» baja exactamente lo que ves, con los mismos filtros.
      </p>
      <form
        className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto_auto_auto]"
        aria-label="Filtros del registro"
        onSubmit={(event) => event.preventDefault()}
      >
        <Select
          label="Persona"
          value={filters.user_id ?? ''}
          onChange={(event) => change({ user_id: event.target.value || undefined })}
        >
          <option value="">Todas</option>
          {(users.data?.items ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name ? `${user.full_name} (${user.email})` : user.email}
            </option>
          ))}
        </Select>
        <Select
          label="Evento"
          value={filters.event ?? ''}
          onChange={(event) => change({ event: event.target.value || undefined })}
        >
          <option value="">Todos</option>
          {EVENTS.map((event) => (
            <option key={event} value={event}>
              {eventLabel(event)}
            </option>
          ))}
        </Select>
        <Input
          label="Desde"
          type="date"
          value={filters.since?.slice(0, 10) ?? ''}
          onChange={(event) => change({ since: startOfDay(event.target.value) })}
        />
        <Input
          label="Hasta"
          type="date"
          value={untilAsDay(filters.until)}
          onChange={(event) => change({ until: endOfDay(event.target.value) })}
        />
        <a
          href={accessLogCsvUrl(filters)}
          download
          className="bg-card text-ink ring-line hover:bg-paper-2 focus-visible:outline-accent inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md px-3.5 text-[14px] font-medium whitespace-nowrap ring-1 ring-inset transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:col-span-2 sm:justify-self-start lg:col-span-1"
        >
          <DownloadIcon size={16} />
          Descargar CSV
        </a>
      </form>

      <Card padded={false}>
        {log.isPending ? <Spinner variant="fill" label="Cargando el registro" /> : null}
        {log.isError ? (
          <ErrorMessage error={log.error} className="m-4">
            No se pudo cargar el registro de accesos
          </ErrorMessage>
        ) : null}
        {log.data && log.data.items.length === 0 ? (
          <EmptyState
            title="Nada con esos filtros"
            hint="Ni entradas ni cambios en ese rango. Amplía las fechas o quita la persona."
          />
        ) : null}
        {log.data && log.data.items.length > 0 ? (
          <>
            <Table label="Entradas del registro" minWidth="0">
              <THead>
                <Tr>
                  <Th>Cuándo</Th>
                  <Th>Qué</Th>
                  <Th>Quién</Th>
                  <Th>Desde</Th>
                  <Th>Detalle</Th>
                </Tr>
              </THead>
              <TBody>
                {log.data.items.map((entry) => (
                  <LogRow key={entry.id} entry={entry} />
                ))}
              </TBody>
            </Table>
            <footer className="border-line-2 text-ink-3 flex items-center justify-between border-t px-4 py-2 text-[13px]">
              <span className="tabular-nums">
                {from}–{to} de {total}
              </span>
              <span className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={offset === 0}
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      offset: Math.max(0, offset - ACCESS_LOG_PAGE),
                    }))
                  }
                >
                  Anteriores
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={to >= total}
                  onClick={() =>
                    setFilters((current) => ({ ...current, offset: offset + ACCESS_LOG_PAGE }))
                  }
                >
                  Siguientes
                </Button>
              </span>
            </footer>
          </>
        ) : null}
      </Card>
    </div>
  );
}

function LogRow({ entry }: { entry: AccessLogEntry }) {
  const failed = entry.event === 'login_failed' || entry.event === 'login_locked';
  return (
    <Tr>
      <Td className="whitespace-nowrap tabular-nums">{formatDate(dateTimeFormat, entry.at)}</Td>
      <Td className={failed ? 'text-danger font-medium' : 'font-medium'}>
        {eventLabel(entry.event)}
      </Td>
      <Td>{entry.user_email ?? <span className="text-ink-4">desconocido</span>}</Td>
      <Td className="text-ink-3 whitespace-nowrap">{entry.ip ?? ''}</Td>
      <Td className="text-ink-3 max-w-[360px] truncate" title={detailText(entry.detail)}>
        {detailText(entry.detail)}
      </Td>
    </Tr>
  );
}
